import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Modal } from '@spirit-testing/component-library';
import {
  TEST_CASE_PRIORITIES,
  TEST_CASE_TYPES,
  TestCase,
  TestCasePriority,
  TestCaseType,
} from '../../../models/test-case.model';
import { SavedTestcaseFilter } from '../../../models/saved-testcase-filter.model';
import { TestcaseLinkFilterService } from '../../../services/testcase-link-filter.service';
import { SavedTestcaseFilterChipsComponent } from './saved-testcase-filter-chips/saved-testcase-filter-chips.component';

@Component({
  selector: 'app-tc-picker-modal',
  imports: [Modal, MatCheckbox, MatIcon, MatMenuModule, TranslatePipe, SavedTestcaseFilterChipsComponent],
  templateUrl: './tc-picker-modal.component.html',
  styleUrl: './tc-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcPickerModalComponent implements AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly filterService = inject(TestcaseLinkFilterService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error' };

  readonly visible = model(false);
  readonly testCases = input<TestCase[]>([]);
  readonly projectId = input<string>('');
  readonly busy = input(false);
  readonly confirm = output<TestCase[]>();

  readonly search = signal('');
  readonly typeFilter = signal<TestCaseType | null>(null);
  readonly priorityFilter = signal<TestCasePriority | null>(null);
  readonly selectedIds = signal<Set<string>>(new Set());

  readonly savedFilters = signal<SavedTestcaseFilter[]>([]);
  readonly activeFilterName = signal<string | null>(null);

  readonly typeOptions = computed<TestCaseType[]>(() => {
    const present = new Set(this.testCases().map((tc) => tc.type));
    return TEST_CASE_TYPES.filter((t) => present.has(t));
  });
  readonly priorityOptions = computed<TestCasePriority[]>(() => {
    const present = new Set(this.testCases().map((tc) => tc.priority));
    return TEST_CASE_PRIORITIES.filter((p) => present.has(p));
  });

  readonly filtered = computed(() => {
    const q = this.search().toLowerCase();
    const type = this.typeFilter();
    const priority = this.priorityFilter();
    return this.testCases().filter(
      (tc) =>
        (!q || tc.title.toLowerCase().includes(q)) &&
        (!type || tc.type === type) &&
        (!priority || tc.priority === priority),
    );
  });
  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly hasSelection = computed(() => this.selectedIds().size > 0);
  readonly canSaveFilter = computed(() => !!(this.search() || this.typeFilter() || this.priorityFilter()));

  @ViewChild('content') contentTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  constructor() {
    effect(() => {
      if (!this.visible()) {
        this.resetFilters();
        this.selectedIds.set(new Set());
      }
    });
    effect(() => {
      const projectId = this.projectId();
      if (this.visible() && projectId) {
        this.loadSavedFilters(projectId);
      }
    });
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.contentTemplate;
    this.cdr.detectChanges();
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelect(tc: TestCase): void {
    this.selectedIds.update((set) => {
      const next = new Set(set);
      if (next.has(tc.id)) next.delete(tc.id);
      else next.add(tc.id);
      return next;
    });
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    this.clearActiveFilter();
  }

  setType(value: TestCaseType | null): void {
    this.typeFilter.set(value);
    this.clearActiveFilter();
  }

  setPriority(value: TestCasePriority | null): void {
    this.priorityFilter.set(value);
    this.clearActiveFilter();
  }

  onFilterSaved(name: string): void {
    const projectId = this.projectId();
    if (!projectId) return;
    this.filterService
      .save(projectId, {
        name,
        search: this.search() || null,
        typ: this.typeFilter(),
        priority: this.priorityFilter(),
        status: null,
      })
      .subscribe({
        next: (saved) => {
          this.savedFilters.update((list) => {
            const rest = list.filter((f) => f.name !== saved.name);
            return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
          });
          this.activeFilterName.set(saved.name);
        },
        error: () => this.notifyError('requirements.testCases.savedFilters.saveError'),
      });
  }

  onFilterSelected(filter: SavedTestcaseFilter): void {
    this.search.set(filter.search ?? '');
    this.typeFilter.set(this.validEnumValue(filter.typ, TEST_CASE_TYPES));
    this.priorityFilter.set(this.validEnumValue(filter.priority, TEST_CASE_PRIORITIES));
    this.activeFilterName.set(filter.name);
  }

  onFilterDeleted(name: string): void {
    const projectId = this.projectId();
    if (!projectId) return;
    this.filterService.delete(projectId, name).subscribe({
      next: () => {
        this.savedFilters.update((list) => list.filter((f) => f.name !== name));
        if (this.activeFilterName() === name) this.activeFilterName.set(null);
      },
      error: () => this.notifyError('requirements.testCases.savedFilters.deleteError'),
    });
  }

  onConfirm(): void {
    const selected = this.testCases().filter((tc) => this.selectedIds().has(tc.id));
    if (selected.length) {
      this.confirm.emit(selected);
    }
    this.visible.set(false);
    this.resetFilters();
    this.selectedIds.set(new Set());
  }

  onCancel(): void {
    this.visible.set(false);
  }

  private clearActiveFilter(): void {
    if (this.activeFilterName() !== null) this.activeFilterName.set(null);
  }

  private validEnumValue<T extends string>(value: string | null, allowed: readonly T[]): T | null {
    return value != null && (allowed as readonly string[]).includes(value) ? (value as T) : null;
  }

  private notifyError(key: string): void {
    this.snackBar.open(this.translate.instant(key), undefined, this.snackError);
  }

  private resetFilters(): void {
    this.search.set('');
    this.typeFilter.set(null);
    this.priorityFilter.set(null);
    this.activeFilterName.set(null);
  }

  private loadSavedFilters(projectId: string): void {
    this.filterService.list(projectId).subscribe({
      next: (filters) => this.savedFilters.set(filters),
      error: () => this.savedFilters.set([]),
    });
  }
}
